import { useState, useCallback, createContext, useContext, type ReactNode } from 'react';
import settings from '../config/settings';

interface ISidebarState {
  sidebarOpen: boolean;
  /** Opens or closes the sidebar with a boolean, toggles it with anything else (a click event). */
  toggleSidebar: (value?: unknown) => void;
}

interface IThemeState {
  theme: string;
  changeTheme: (selectedTheme: string) => void;
}

export interface IAppContextValue extends ISidebarState, IThemeState {
  locale?: string;
}

interface IAppContextProviderProps {
  children: ReactNode;
  locale: string;
}

const sidebarDefaults: ISidebarState = {
  sidebarOpen: false,
  toggleSidebar: () => {},
};

const useSidebarState = (): ISidebarState => {
  const [sidebarOpen, setSidebar] = useState(sidebarDefaults.sidebarOpen);
  const toggleSidebar = useCallback(
    (v?: unknown) => setSidebar(typeof v === 'boolean' ? v : (s) => !s),
    [],
  );
  return { sidebarOpen, toggleSidebar };
};

const themes = ['primary', 'green'];

const themeDefaults: IThemeState = {
  theme: 'primary',
  changeTheme: () => {},
};

const useTheme = (): IThemeState => {
  const [theme, setTheme] = useState(themeDefaults.theme);
  const changeTheme = useCallback((selectedTheme: string) => {
    if (themes.includes(selectedTheme)) setTheme(selectedTheme);
  }, []);
  return { theme, changeTheme };
};

export const AppContext = createContext<IAppContextValue>({
  locale: settings.defaultLocale,
  ...sidebarDefaults,
  ...themeDefaults,
});

export const AppContextProvider = ({ children, ...value }: IAppContextProviderProps) => {
  const sidebarState = useSidebarState();
  const themeManagement = useTheme();
  return (
    // `value` is a rest prop rebuilt on every render, so a useMemo here would be
    // cosmetic; stabilising it requires reshaping the provider API.
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <AppContext.Provider value={{ ...value, ...sidebarState, ...themeManagement }}>
      {children}
    </AppContext.Provider>
  );
};

export const AppContextConsumer = AppContext.Consumer;

export const useAppContext = () => useContext(AppContext);
